import React, { useEffect, useMemo, useState } from 'react';
import { bgLineGridColorDark, bgLineGridColorLight, ContaAReceber, corBgGrid } from '../../Utils/Types';
import { createTheme, ThemeProvider } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUser } from '../Redux/userSlice';
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import moment from 'moment';
import { Funcoes } from '../../Utils/Funcoes';


type Props = {
    duplicata: ContaAReceber[],
    tipo: TipoStatus,
}

type VisibilityState = Record<string, boolean>

export type TipoStatus = | "A" | "L"

interface RowData {
    id: number,
    data_vencimento: string,
    dt_baixa: string,
    dt_cadastro: string,
    dt_last_update: string,
    id_cliente: number,
    id_user_baixa: string,
    id_user_insert: string,
    id_user_last_update: string,
    id_venda: number,
    id_bordero: number,
    nome_user_last_update: string,
    nome_user_baixa: string,
    nome_user_insert: string,
    nome_cliente: string,
    obs_baixa: string
    observacao: number,
    serie: number,
    qtdDup: number,
    valor: number,
    valor_original: number,
    vl_desconto: string,
    vl_juros: number,
    selected: boolean,
    taxa_juros: number,
    valorJuros: number,
}

export const GridRelContaAReceberDia = React.memo(({ duplicata, tipo }: Props) => {

    const defaultJson = useMemo(() => {
        return (

            localStorage.getItem("@commerce/column_grid_ctareceber_relatorio")
                ? JSON.parse(localStorage.getItem("@commerce/column_grid_ctareceber_relatorio") || '')
                : {
                    id: true,
                    nome_cliente: true,
                    dt_cadastro: true,
                    data_vencimento: true,
                    serie: true,
                    valor: true,
                    valor_original: true,
                    taxa_juros: true,
                    valorJuros: true,
                    observacao: true,
                    observacao_baixa: false,
                    dt_baixa: false,
                    id_bordero: false
                }
        )
    }, [])

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultJson)
    const [duplicatas, setDuplicatas] = useState<ContaAReceber[]>([])

    let user = useSelector(state => selectUser(state));

    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- EFFECT -------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        setDuplicatas([])

        const timer = setTimeout(() => {
            const newColumnVisibility = { ...columnVisibility }

            // Sempre atualiza essas colunas com base no tipo
            newColumnVisibility.observacao = tipo === "A"
            newColumnVisibility.taxa_juros = tipo === "A"
            newColumnVisibility.valorJuros = tipo === "A"

            newColumnVisibility.id_bordero = tipo === "L"
            newColumnVisibility.observacao_baixa = tipo === "L"
            newColumnVisibility.dt_baixa = tipo === "L"

            setColumnVisibility(newColumnVisibility)
            clearTimeout(timer)
        }, 150)

        return () => clearTimeout(timer)

    }, [tipo])

    useEffect(() => {
        setDuplicatas(duplicata)
    }, [duplicata])

    useEffect(() => {
        localStorage.setItem("@commerce/column_grid_ctareceber_relatorio", JSON.stringify(columnVisibility))
    }, [columnVisibility])


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TEMAS --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const theme = createTheme({
        palette: {
            mode: user.modeDark,
            background: {
                default: user.modeDark === 'light' ? '#fff' : '#475569',
            },
        },
    })

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- COLUNAS ------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const calcularTaxa = (rowData: RowData): number => {
        const hoje = new Date();
        const dias = parseInt(localStorage.getItem("@ecommerce/diasJuros") || '0')

        // Cria a data de vencimento
        const vencimentoBase = new Date(rowData.data_vencimento);

        // Soma os dias
        const vencimento = new Date(vencimentoBase);
        vencimento.setDate(vencimento.getDate() + 1 + dias);

        const taxa = (rowData.data_vencimento && vencimento < hoje)
            ? parseFloat(localStorage.getItem("@ecommerce/taxa_juros") || '0')
            : 0;

        return taxa;
    }


    const columns = useMemo<MRT_ColumnDef<ContaAReceber>[]>(() => [

        {
            accessorKey: 'id', visibleInShowHideMenu: true, header: 'Duplicata', size: 150, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.id ? rowData.id : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'nome_cliente', visibleInShowHideMenu: true, header: 'Cliente', size: 150, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.nome_cliente ? rowData.nome_cliente : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'dt_cadastro', visibleInShowHideMenu: true, header: 'Data de Emissão', size: 150, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.dt_cadastro ? moment(rowData.dt_cadastro).format('DD/MM/YYYY') : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'data_vencimento',
            header: 'Data de Vencimento', visibleInShowHideMenu: true, size: 150, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.data_vencimento ? moment(rowData.data_vencimento).format('DD/MM/YYYY') : <span className='text-red-400'>N/D</span>}</span>)


            }
        },

        {
            accessorKey: 'dt_baixa',
            header: 'Data da Baixa', visibleInShowHideMenu: tipo === 'L' ? true : false, size: 150, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.dt_baixa ? moment(rowData.dt_baixa).format('DD/MM/YYYY') : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'serie', visibleInShowHideMenu: tipo === 'A' ? true : false /*true*/, header: 'Série', size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.serie ? rowData.serie : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'observacao_baixa', visibleInShowHideMenu: tipo === 'L' ? true : false, header: 'Observação da Baixa', size: 200, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.obs_baixa ? rowData.obs_baixa : <span className='text-red-400'>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'valor_original', visibleInShowHideMenu: true, header: 'valor Original', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.valor_original ? Funcoes.numberFormat(rowData.valor_original, 'currency') : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'valor', visibleInShowHideMenu: true, header: 'Valor', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.valor ? Funcoes.numberFormat(rowData.valor, 'currency') : <span className='text-red-400'>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'taxa_juros', visibleInShowHideMenu: tipo === 'A' ? true : false /*true*/, header: 'Juros(%) ao Mês', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {
                const rowData = cell.row.original as RowData;

                let taxa = calcularTaxa(rowData)

                return (
                    <span>
                        {taxa + "%"}
                    </span>
                );
            }
        },

        {
            accessorKey: 'valorJuros', visibleInShowHideMenu: tipo === 'A' ? true : false, header: 'Valor Com Juros', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {
                const rowData = cell.row.original as RowData;
                const valor = Number(rowData.valor ?? 0);
                const hoje = new Date();
                let valorComJuros = valor;

                if (rowData.data_vencimento && valor > 0) {
                    const taxaPadrao = parseFloat(localStorage.getItem("@ecommerce/taxa_juros") || '0')
                    const dias = Number(localStorage.getItem("@ecommerce/diasJuros") || 0)

                    // Soma dias à data de vencimento
                    const vencimentoBase = new Date(rowData.data_vencimento);
                    const vencimento = new Date(vencimentoBase);
                    vencimento.setDate(vencimento.getDate() + 1 + dias);

                    // Normaliza as horas para evitar problema de fuso
                    const hojeZero = new Date(hoje);
                    hojeZero.setHours(0, 0, 0, 0);
                    const vencZero = new Date(vencimento);
                    vencZero.setHours(0, 0, 0, 0);

                    const diffTime = hojeZero.getTime() - vencZero.getTime();
                    const diffDias = Math.max(Math.floor(diffTime / (1000 * 60 * 60 * 24)), 0);

                    if (diffDias > 0) {

                        const taxaPercent = taxaPadrao          // ex.: 5 = 5% ao mês
                        const taxaDiaria = (taxaPercent / 100) / 30;    // proporcional ao mês
                        const juros = valor * taxaDiaria * diffDias;    // juros simples
                        valorComJuros = valor + juros;
                    }
                }

                return (
                    <span>
                        {valor
                            ? Funcoes.numberFormat(valorComJuros, 'currency')
                            : <span className='text-red-400'>N/D</span>}
                    </span>
                );
            }
        },

        {
            accessorKey: 'id_bordero', visibleInShowHideMenu: tipo === 'L' ? true : false /*true*/, header: 'Bordero', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.id_bordero ? rowData.id_bordero : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'observacao', visibleInShowHideMenu: tipo === 'A' ? true : false, header: 'Observação', size: 300, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.observacao ? rowData.observacao : <span className='text-red-400'>N/D</span>}</span>)
            }
        },

    ], [duplicata]
    )


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TABLE --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const table = useMaterialReactTable({
        paginationDisplayMode: 'pages',
        columns: columns,
        data: duplicatas,
        autoResetPageIndex: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,
        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        localization: MRT_Localization_PT_BR,
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableSorting: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        onColumnVisibilityChange: setColumnVisibility,
        state: { columnVisibility },
        initialState: {
            pagination: { pageIndex: 0, pageSize: 10 },  // Define 10 elementos por página
        },
        muiPaginationProps: {
            rowsPerPageOptions: [10],  // Apenas uma opção para o seletor: 10
        },

    })

    return (
        <>

            <ThemeProvider theme={theme}>
                <MaterialReactTable table={table} />
            </ThemeProvider>

        </>
    )

})